import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { authClient } from './api/client.js';
import { Shell } from './components/Shell.js';
import { I18nProvider, useDictionary } from './i18n/context.js';
import { ActivityRoute } from './routes/activity.js';
import { AiConnectionsRoute } from './routes/ai-connections.js';
import { BrandContextRoute } from './routes/brand-context.js';
import { CalendarRoute } from './routes/calendar.js';
import { CampaignsRoute } from './routes/campaigns.js';
import { HomeRoute } from './routes/home.js';
import { ProjectRoute } from './routes/project.js';
import { ProjectsRoute } from './routes/projects.js';
import { ReviewRoute } from './routes/review.js';
import { sanitizeRedirectTarget, SignInRoute } from './routes/sign-in.js';

// Phase 1's shipped screens (apps/web/src/routes/{home,review,project,
// projects,campaigns,activity,ai-connections,brand-context,calendar}.tsx)
// -- the project setup wizard and settings are out of scope (see the
// task-19 and task-4-6 reports), so every other structure-spec route
// (section 4) falls back to Home rather than 404ing. Shell.tsx renders nav
// items for those still-missing screens as a disabled control rather than a
// link, so no nav item actually depends on this catch-all silently
// substituting Home.

const SessionLoading = (): React.JSX.Element => {
  const dict = useDictionary();
  return <p className="t-meta" style={{ padding: 'var(--sp-6)' }}>{dict.ui.auth.sessionLoading}</p>;
};

// ---------------------------------------------------------------------------
// The application's own authentication boundary (structure spec 5.3, which
// used to say Cloudflare Access owned this and now describes this instead).
// `authClient.useSession()` (apps/web/src/api/client.ts) reads the same
// session cookie `requireSession` (apps/worker/src/auth/session.ts) checks
// on the Worker, reactively -- every subscriber (this gate, Shell's
// sign-out) re-renders the moment a sign-in or sign-out resolves.
//
// With no live session, *no* product route is reachable, including deep
// links -- the catch-all below redirects to `/sign-in` and carries the
// attempted path in `?redirect=` so a successful sign-in returns the
// operator to where they were actually going, rather than always to Home.
//
// The two branches below share ownership of that `?redirect=` value:
// unauthenticated encodes it, authenticated decodes it. This matters
// because `routes/sign-in.tsx` never calls `navigate()` itself (see its own
// comment) -- the moment `authClient.signIn.email` resolves, `useSession()`
// here flips this component from the unauthenticated branch to the
// authenticated one *without the URL having changed on its own*, so it is
// still sitting on `/sign-in?redirect=...`. The authenticated branch's own
// `/sign-in` route is what actually reads that query string and redirects
// from it -- a plain `<Navigate to="/home" />` there, as an earlier version
// of this file had, would silently discard the operator's original
// destination on every sign-in.
// ---------------------------------------------------------------------------
const AppRoutes = (): React.JSX.Element => {
  const location = useLocation();
  const { data: session, isPending } = authClient.useSession();

  if (isPending) return <SessionLoading />;

  if (!session) {
    const attemptedPath = `${location.pathname}${location.search}`;
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInRoute />} />
        <Route path="*" element={<Navigate to={`/sign-in?redirect=${encodeURIComponent(attemptedPath)}`} replace />} />
      </Routes>
    );
  }

  const postSignInTarget = sanitizeRedirectTarget(new URLSearchParams(location.search).get('redirect'));

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/sign-in" element={<Navigate to={postSignInTarget} replace />} />
        <Route path="/home" element={<HomeRoute />} />
        <Route path="/calendar" element={<CalendarRoute />} />
        <Route path="/review/:variantId" element={<ReviewRoute />} />
        <Route path="/campaigns" element={<CampaignsRoute />} />
        <Route path="/projects" element={<ProjectsRoute />} />
        <Route path="/activity" element={<ActivityRoute />} />
        <Route path="/ai-connections" element={<AiConnectionsRoute />} />
        <Route path="/projects/:projectId" element={<ProjectRoute />} />
        <Route path="/projects/:projectId/brand-context" element={<BrandContextRoute />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Shell>
  );
};

export const App = (): React.JSX.Element => (
  <I18nProvider>
    <AppRoutes />
  </I18nProvider>
);
